import { html, nothing, TemplateResult } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { DisplayMode } from '@api-consent/types/common';
import type { TefComs4csMicroFrontend } from '@src/components/tef-coms4cs';
import { localDate, localTime, replaceElementsBound } from '@src/components/templates/util';
import { comsStore } from '@src/state/ComsStore';
import { ConsentGroupStore } from '@src/state/ConsentGroupStore';
import type { ConsentStore } from '@src/state/ConsentStore';

function computeClassName(consent: ConsentStore): string | undefined {
  if (consent.modifiedFromInitialSelfOrOneOfTheFeatures) {
    return 'edited';
  }
  if (consent.deprecated) {
    return 'outdated';
  }
  if (consent.granted && consent.hasNotGrantedFeature) {
    return 'part_agreed';
  }
  if (consent.granted) {
    return 'agreed';
  }
  if (consent.granted === undefined) {
    return undefined;
  }
  if (!consent.granted) {
    return 'rejected';
  }
  return undefined;
}

export function consentTemplate(this: TefComs4csMicroFrontend, consent: ConsentStore, group: ConsentGroupStore):
TemplateResult {
  const featureTemplate = this.getTemplateFn('featureTemplate');
  const className = computeClassName(consent);
  const iconType = consent.granted ? 'check' : 'cross';
  const disabled = (comsStore.identity?.readonly || consent?.deprecated) ?? false;

  const lastChangeAtHtml = html`
    <small>
      <tef-coms-icon icon="clock"></tef-coms-icon>
      <a
        role="button"
        @click=${() => { /* eslint-disable @typescript-eslint/indent */
          comsStore.history!.configId = consent.configId;
          comsStore.history!.consentShortScope = consent.shortScope;
        } /* eslint-enable @typescript-eslint/indent */}
      >
        Letzte Änderung am
        ${localDate(consent.date)} um ${localTime(consent.date)} Uhr über ${consent.clientDisplay ?? consent.client}
      </a>&nbsp;
    </small>
  `;

  const consentContent = html`
    <div>
      ${unsafeHTML(replaceElementsBound(this, consent.scope))}
      ${comsStore.config?.showVersions ? html`<small>(Vers. ${consent.version})</small>` : nothing}
    </div>
    ${consent.features.map((f) => html`${featureTemplate(f, !!consent.deprecated)}`)}
    ${consent.client && consent.date ? lastChangeAtHtml : nothing}
  `;

  const checkboxHtml = html`
    <tef-coms-multi-checkbox
      value="${ifDefined(consent.granted)}"
      @change=${(event: { detail: boolean; }) => { consent.change(event.detail); }}
      ?disabled="${(comsStore.identity?.readonly || consent.deprecated) ?? false}"
    >
      ${consentContent}
    </tef-coms-multi-checkbox>
  `;

  const iconHtml = html`
    <div>
      <tef-coms-icon .icon="${iconType}" size="l"></tef-coms-icon>
      ${consentContent}
    </div>
  `;

  let consentHtml;
  let needBorder = true;
  if (disabled) {
    consentHtml = iconHtml;
  } else if (group.displayMode === DisplayMode.TEXT) {
    needBorder = false;
    consentHtml = html`<div>${consentContent}</div>`;
  } else if (group.displayMode === DisplayMode.LIST) {
    needBorder = false;
    consentHtml = html`<ul class="list--unordered"><li>${consentContent}</li></ul>`;
  } else {
    consentHtml = checkboxHtml;
  }

  return html`
    ${consent.hintBefore ? html`<div class="coms-hint-before">${unsafeHTML(consent.hintBefore)}</div>` : nothing}
      <div class="${needBorder ? 'coms-panel' : ''} ${className ? `coms-panel--${className}` : ''}">
        ${consent.modeconstraint === 'edit' ? html`<small class="join-hint">Diese Einwilligung ist aus der Zusammenführung der vorhergehenden Einwilligungen entstanden</small>` : nothing}
        ${consentHtml}
      </div>
    ${consent.hintAfter ? html`<div class="coms-hint-after">${unsafeHTML(consent.hintAfter)}</div>` : nothing}
  `;
}
